import { createTelemetryObject, createTelemetry } from '@shared/core/analytics';
import type { SortOption } from '@shared/constants/accommodations';
import type { Maybe } from '@graphql/generated-base';

export const listingsPanelTelemetry = createTelemetryObject({
  actions: {
    seeAvailabilityClicked: ({ joyPlaceId, hotelName, name = 'seeAvailabilityClicked' }: { joyPlaceId?: string; hotelName?: string; name?: string }) => ({
      category: 'accommodations',
      action: 'TravelButtonInteracted',
      extraInfo: {
        name,
        joyPlaceId,
        from: 'Accommodations Console',
        hotelName,
      },
    }),
    hotelBookingRedirect: ({
      joyPlaceId,
      hotelName,
      destinationURL,
      destinationDomain,
      destinationName,
      from = 'Accommodations Console',
      name = 'hotelBookingRedirect',
      utm_source,
      utm_medium,
      utm_campaign,
    }: {
      joyPlaceId?: string;
      hotelName?: string;
      destinationURL: string;
      destinationDomain: string;
      destinationName: string;
      from?: string;
      name?: string;
      utm_source?: string;
      utm_medium?: string;
      utm_campaign?: string;
    }) => ({
      category: 'travel',
      action: 'GuestHotelBookingRedirect',
      label: 'hotelSearch',
      extraInfo: {
        name,
        joyPlaceId,
        hotelName,
        destinationURL: destinationURL || '',
        destinationDomain: destinationDomain || '',
        destinationName: destinationName || '',
        from,
        utm_source,
        utm_medium,
        utm_campaign,
      },
    }),
    pickedByCoupleToggled: ({ state }: { state: boolean }) => ({
      category: 'accommodations',
      action: 'TravelButtonInteracted',
      extraInfo: {
        name: 'pickedByCoupleToggled',
        state,
        from: 'Accommodations Console',
      },
    }),
    joyTravelBenefitsFilterChanged: ({ action, popular }: { action: 'added' | 'removed'; popular?: boolean }) => ({
      category: 'accommodations',
      action: 'TravelButtonInteracted',
      extraInfo: {
        name: 'joyTravelBenefitsFilterChanged',
        action,
        from: 'Accommodations Console',
        ...(popular && { popular }),
      },
    }),
    freeCancellationFilterChanged: ({ action, popular }: { action: 'added' | 'removed'; popular?: boolean }) => ({
      category: 'accommodations',
      action: 'TravelButtonInteracted',
      extraInfo: {
        name: 'freeCancellationFilterChanged',
        action,
        from: 'Accommodations Console',
        ...(popular && { popular }),
      },
    }),
    showAvailableOnlyFilterChanged: ({ action }: { action: 'added' | 'removed' }) => ({
      category: 'accommodations',
      action: 'TravelButtonInteracted',
      extraInfo: {
        name: 'showAvailableOnlyFilterChanged',
        action,
        from: 'Accommodations Console',
      },
    }),
    guestRatingFilterChanged: ({ rating, action, popular }: { rating: number; action: 'added' | 'removed'; popular?: boolean }) => ({
      category: 'accommodations',
      action: 'TravelButtonInteracted',
      extraInfo: {
        name: 'guestRatingFilterChanged',
        rating,
        action,
        from: 'Accommodations Console',
        ...(popular && { popular }),
      },
    }),
    starRatingFilterChanged: ({ rating, action }: { rating: number; action: 'added' | 'removed' }) => ({
      category: 'accommodations',
      action: 'TravelButtonInteracted',
      extraInfo: {
        name: 'starRatingFilterChanged',
        rating,
        action,
        from: 'Accommodations Console',
      },
    }),
    facilityFilterChanged: ({ facility, action, popular }: { facility: string; action: 'added' | 'removed'; popular?: boolean }) => ({
      category: 'accommodations',
      action: 'TravelButtonInteracted',
      extraInfo: {
        name: 'facilityFilterChanged',
        facility,
        action,
        from: 'Accommodations Console',
        ...(popular && { popular }),
      },
    }),
    propertyCategoryFilterChanged: ({ propertyCategory, action, popular }: { propertyCategory: string; action: 'added' | 'removed'; popular?: boolean }) => ({
      category: 'accommodations',
      action: 'TravelButtonInteracted',
      extraInfo: {
        name: 'propertyCategoryFilterChanged',
        propertyCategory,
        action,
        from: 'Accommodations Console',
        ...(popular && { popular }),
      },
    }),
    brandFilterChanged: ({ brand, action }: { brand: string; action: 'added' | 'removed' }) => ({
      category: 'accommodations',
      action: 'TravelButtonInteracted',
      extraInfo: {
        name: 'brandFilterChanged',
        brand,
        action,
        from: 'Accommodations Console',
      },
    }),
    priceRangeFilterChanged: ({ minPrice, maxPrice }: { minPrice: number; maxPrice: number }) => ({
      category: 'accommodations',
      action: 'TravelButtonInteracted',
      extraInfo: {
        name: 'priceRangeFilterChanged',
        minPrice,
        maxPrice,
        from: 'Accommodations Console',
      },
    }),
    allFiltersReset: () => ({
      category: 'accommodations',
      action: 'TravelButtonInteracted',
      extraInfo: {
        name: 'allFiltersReset',
        from: 'Accommodations Console',
      },
    }),
    propertyNameSearched: ({ keyword, resultCount }: { keyword: string; resultCount: number }) => ({
      category: 'accommodations',
      action: 'TravelButtonInteracted',
      extraInfo: {
        name: 'propertyNameSearched',
        keyword,
        resultCount,
        from: 'Accommodations Console',
      },
    }),
    sortOptionChanged: ({ sortOption }: { sortOption: SortOption }) => ({
      category: 'accommodations',
      action: 'TravelButtonInteracted',
      extraInfo: {
        name: 'sortOptionChanged',
        sortOption,
        from: 'Accommodations Console',
      },
    }),
    destinationSearched: ({
      googlePlaceId,
      locationName,
      responseTimeMs,
      resultCount,
    }: {
      googlePlaceId?: Maybe<string>;
      locationName?: Maybe<string>;
      responseTimeMs: number;
      resultCount: number;
    }) => ({
      category: 'accommodations',
      action: 'TravelButtonInteracted',
      extraInfo: {
        name: 'destinationSearched',
        googlePlaceId,
        locationName,
        responseTimeMs,
        resultCount,
        from: 'Accommodations Console',
      },
    }),
  },
});

const { TelemetryProvider, useTelemetry } = createTelemetry(listingsPanelTelemetry, { eventId: '', section: '' });

export { TelemetryProvider, useTelemetry as useListingsPanelTelemetry };
